import React, { useEffect, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { apiErrorMessage } from "Common/constants/apiErrors";

interface PaymentSettings {
  minimum_payment_percentage: number;
  minimum_with_sales_manager_approval: number;
  maximum_with_sales_manager_approval: number;
  minimum_exit_percentage: number;
}

const Index = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // دریافت تنظیمات فعلی
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get("https://portal-api.mahanpanel.com/payment-minimum/", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // مقادیر را به عدد تبدیل می‌کنیم (API ممکن است رشته برگرداند)
        const data = res.data;
        validation.setValues({
          minimum_payment_percentage: parseFloat(data.minimum_payment_percentage) || 0,
          minimum_with_sales_manager_approval: parseFloat(data.minimum_with_sales_manager_approval) || 0,
          maximum_with_sales_manager_approval: parseFloat(data.maximum_with_sales_manager_approval) || 0,
          minimum_exit_percentage:
            data.minimum_exit_percentage === null || data.minimum_exit_percentage === undefined
              ? 100
              : Number.isFinite(Number(data.minimum_exit_percentage))
                ? Number(data.minimum_exit_percentage)
                : 100,
        });
        setErrorMessage("");
      } catch (error: any) {
        console.error("Error fetching settings:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        setErrorMessage("مشکلی در دریافت تنظیمات حداقل پرداخت رخ داده است.");
      } finally {
        setInitialLoading(false);
      }
    };
    fetchSettings();
  }, [navigate]);

  // فرم تنظیمات
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      minimum_payment_percentage: 0,
      minimum_with_sales_manager_approval: 0,
      maximum_with_sales_manager_approval: 0,
      minimum_exit_percentage: 100,
    },
    validationSchema: Yup.object({
      minimum_payment_percentage: Yup.number()
        .typeError("مقدار باید عدد باشد")
        .min(0, "حداقل ۰")
        .max(100, "حداکثر ۱۰۰")
        .required("لطفاً حداقل درصد پرداخت را وارد کنید"),
      minimum_with_sales_manager_approval: Yup.number()
        .typeError("مقدار باید عدد باشد")
        .min(0, "حداقل ۰")
        .max(100, "حداکثر ۱۰۰")
        .required("لطفاً حداقل با تأیید مدیر فروش را وارد کنید"),
      maximum_with_sales_manager_approval: Yup.number()
        .typeError("مقدار باید عدد باشد")
        .min(0, "حداقل ۰")
        .max(100, "حداکثر ۱۰۰")
        .required("لطفاً حداکثر با تأیید مدیر فروش را وارد کنید")
        .test(
          "max-greater-than-min",
          "حداکثر باید بزرگتر یا مساوی حداقل باشد",
          function (value) {
            const min = this.parent.minimum_with_sales_manager_approval;
            return value >= min;
          }
        ),
      minimum_exit_percentage: Yup.number()
        .typeError("مقدار باید عدد باشد")
        .min(0, "حداقل ۰")
        .max(100, "حداکثر ۱۰۰")
        .required("لطفاً حداقل درصد خروج از انبار را وارد کنید"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const payload = {
          minimum_payment_percentage: Number(values.minimum_payment_percentage),
          minimum_with_sales_manager_approval: Number(values.minimum_with_sales_manager_approval),
          maximum_with_sales_manager_approval: Number(values.maximum_with_sales_manager_approval),
          minimum_exit_percentage: Number(values.minimum_exit_percentage),
        };

        const res = await axios.put(
          "https://portal-api.mahanpanel.com/payment-minimum/",
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (res.status === 200) {
          Swal.fire({
            icon: "success",
            title: "عملیات موفق",
            text: "تنظیمات حداقل پرداخت با موفقیت ذخیره شد",
            confirmButtonText: "باشه",
          });
        }
      } catch (error: any) {
        console.error("Error saving settings:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        if (error.response?.status === 422) {
          // پیغام خطای سرور انگلیسی است؛ apiErrorMessage آن را به جمله‌ی فارسی
          // گویا تبدیل می‌کند (تکراری بودن کد ملی/شماره همراه، فیلد الزامی و ...).
          let msg = apiErrorMessage(error, "اطلاعات وارد شده نامعتبر است");
          Swal.fire({
            icon: "error",
            title: "خطا",
            text: msg,
            confirmButtonText: "باشه",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "خطا",
            text: "مشکل در ذخیره تنظیمات",
            confirmButtonText: "باشه",
          });
        }
      } finally {
        setLoading(false);
      }
    },
  });

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
          <p className="mt-2 text-gray-500">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <BreadCrumb pageTitle="حداقل پرداخت" title="داده‌های اولیه" />

      {errorMessage && (
        <div className="text-center text-red-500 mb-3">{errorMessage}</div>
      )}

      <div className="card">
        <div className="card-body">
          <h6 className="mb-4 text-15 font-bold">تنظیمات حداقل پرداخت</h6>

          <form onSubmit={validation.handleSubmit} className="grid grid-cols-1 gap-4 xl:grid-cols-4">
            {/* حداقل درصد پرداخت */}
            <div className="dark:text-white text-gray-800">
              <label htmlFor="minimum_payment_percentage" className="block text-base font-medium mb-1">
                حداقل درصد پرداخت:
              </label>
              <input
                type="number"
                id="minimum_payment_percentage"
                className="form-input font-[12px] w-full"
                name="minimum_payment_percentage"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.minimum_payment_percentage}
                min="0"
                max="100"
              />
              {validation.touched.minimum_payment_percentage &&
                validation.errors.minimum_payment_percentage && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.minimum_payment_percentage === "string"
                      ? validation.errors.minimum_payment_percentage
                      : ""}
                  </p>
                )}
              <p className="text-gray-400 text-xs mt-1">مقدار بین ۰ تا ۱۰۰ درصد</p>
            </div>

            <div className="dark:text-white text-gray-800">
              <label htmlFor="minimum_exit_percentage" className="block text-base font-medium mb-1">
                حداقل درصد مانده برای خروج هر قلم:
              </label>
              <input
                type="number"
                id="minimum_exit_percentage"
                className="form-input font-[12px] w-full"
                name="minimum_exit_percentage"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.minimum_exit_percentage}
                min="0"
                max="100"
                step="0.01"
              />
              {validation.touched.minimum_exit_percentage && validation.errors.minimum_exit_percentage && (
                <p className="text-red-400 mt-1 text-sm">{String(validation.errors.minimum_exit_percentage)}</p>
              )}
              <p className="text-gray-400 text-xs mt-1">مقدار بین ۰ تا ۱۰۰ درصد</p>
            </div>

            {/* حداقل با تأیید مدیر فروش */}
            <div className="dark:text-white text-gray-800">
              <label htmlFor="minimum_with_sales_manager_approval" className="block text-base font-medium mb-1">
                حداقل با تأیید مدیر فروش:
              </label>
              <input
                type="number"
                id="minimum_with_sales_manager_approval"
                className="form-input font-[12px] w-full"
                name="minimum_with_sales_manager_approval"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.minimum_with_sales_manager_approval}
                min="0"
                max="100"
              />
              {validation.touched.minimum_with_sales_manager_approval &&
                validation.errors.minimum_with_sales_manager_approval && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.minimum_with_sales_manager_approval === "string"
                      ? validation.errors.minimum_with_sales_manager_approval
                      : ""}
                  </p>
                )}
              <p className="text-gray-400 text-xs mt-1">مقدار بین ۰ تا ۱۰۰ درصد</p>
            </div>

            {/* حداکثر با تأیید مدیر فروش */}
            <div className="dark:text-white text-gray-800">
              <label htmlFor="maximum_with_sales_manager_approval" className="block text-base font-medium mb-1">
                حداکثر با تأیید مدیر فروش:
              </label>
              <input
                type="number"
                id="maximum_with_sales_manager_approval"
                className="form-input font-[12px] w-full"
                name="maximum_with_sales_manager_approval"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.maximum_with_sales_manager_approval}
                min="0"
                max="100"
              />
              {validation.touched.maximum_with_sales_manager_approval &&
                validation.errors.maximum_with_sales_manager_approval && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.maximum_with_sales_manager_approval === "string"
                      ? validation.errors.maximum_with_sales_manager_approval
                      : ""}
                  </p>
                )}
              <p className="text-gray-400 text-xs mt-1">مقدار بین ۰ تا ۱۰۰ درصد</p>
            </div>

            {/* دکمه ذخیره */}
            <div className="xl:col-span-3 flex justify-start mt-2">
              <button
                type="submit"
                disabled={loading}
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <span className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-white border-t-transparent mr-2"></span>
                    در حال ذخیره...
                  </>
                ) : (
                  "ذخیره تغییرات"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Index;
